import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ProductDetailCard from "@/components/ProductDetailCard/ProductDetailCard";
import Nav from "@/components/Header/Nav/Nav";
import Footer from "@/components/Footer/Footer";
import styles from "./ProductDetailPage.module.scss";
import ProductCard from "@/components/ProductCard/ProductCard";
import { useCart } from "@/hooks/useCart";
import CardSlider from "@/components/Slider/Slider";
import { Helmet } from "react-helmet-async";
import { formatName } from "@/utils/formatName";
import { getSiteConfig } from "@/config/siteConfigs";

const stripHtml = (html: string): string => {
  return html
    .replace(/<[^>]*>/g, "")
    .replace(/\s+/g, " ")
    .trim();
};

// Розумне обрізання тексту до maxLength символів
// Намагається закінчити на повному реченні або слові
const truncateDescription = (text: string, maxLength: number = 150): string => {
  if (text.length <= maxLength) return text;

  // Шукаємо останню крапку, знак оклику або питання до maxLength
  const truncated = text.slice(0, maxLength);
  const lastSentenceEnd = Math.max(
    truncated.lastIndexOf("."),
    truncated.lastIndexOf("!"),
    truncated.lastIndexOf("?"),
  );

  // Якщо знайшли кінець речення після 50 символів - використовуємо його
  if (lastSentenceEnd > 50) {
    return text.slice(0, lastSentenceEnd + 1);
  }

  // Інакше обрізаємо по останньому пробілу і додаємо "..."
  const lastSpace = truncated.lastIndexOf(" ");
  if (lastSpace > 50) {
    return text.slice(0, lastSpace) + "...";
  }

  // Якщо немає пробілів - просто обрізаємо
  return truncated + "...";
};

const CRM_API = "https://crm.hixa.eu/external/goodsapi/";
const CRM_TOKEN = "DH345DFGDGb32FDsf03876SDFSdf13";

interface Product {
  goid: string;
  catid: string;
  name: string;
  descr: string;
  nettoprice: number;
  priceexch: number;
  currency: string;
  table: string;
  files: { image?: { [key: number]: string } };
}

const ProductDetailPage: React.FC = () => {
  const { id, section } = useParams<{ id: string; section: string }>();
  const { t } = useTranslation("pages");
  const siteConfig = getSiteConfig();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [similarProducts, setSimilarProducts] = useState<Product[]>([]);
  const { addToCart } = useCart();
  const formattedName = formatName(product?.name || "");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) {
        setError(t("productDetail.errors.invalidId"));
        setLoading(false);
        return;
      }

      try {
        const url =
          section === "service"
            ? `${CRM_API}?table=service&legid=1&language=${siteConfig.language}&type=good&goid=${id}`
            : `${CRM_API}?type=good&goid=${id}&legid=1&language=${siteConfig.language}`;

        const searchResponse = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${CRM_TOKEN}`,
            Accept: "application/json",
          },
        });

        const searchData = await searchResponse.json();
        if (!searchData || searchData.data.length === 0) {
          setError(t("productDetail.errors.notFound"));
        } else {
          const product = searchData.data[0];
          setProduct(product);
          fetchSimilarProducts(product.goid, product.goid);
        }
      } catch (error) {
        setError(t("productDetail.errors.fetchError"));
      } finally {
        setLoading(false);
      }
    };

    const fetchSimilarProducts = async (
      goodid: string,
      currentGoid: string,
    ) => {
      try {
        const requestBody: any = {
          type: "good",
          onlyprice: "1",
          start: "0",
          limit: "10",
          goodid: goodid,
          legid: "1",
          language: siteConfig.language,
        };

        if (section === "service") {
          requestBody.table = "service";
        }

        const response = await fetch(CRM_API, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${CRM_TOKEN}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        });

        const data = await response.json();
        const filtered = data?.data?.filter(
          (item: Product) => item.goid !== currentGoid,
        );

        setSimilarProducts(filtered?.slice(0, 5) || []);
      } catch (err) {
        console.error("Error loading similar products:", err);
      }
    };

    fetchProduct();
  }, [id, section, siteConfig.language, t]);

  if (loading) return <p>{t("productDetail.loading")}</p>;
  if (error) return <p>{error}</p>;
  if (!product) return <p>{t("productDetail.errors.missingData")}</p>;

  // Визначаємо чи це мехатронік
  const isMechatronic =
    product.name.toLowerCase().includes("mechatronik") ||
    product.name.toLowerCase().includes("мехатронік") ||
    product.name.toLowerCase().includes("mechatronika");

  // Отримуємо локалізоване ключове слово
  const titleKeyword = isMechatronic
    ? t("productDetail.meta.titleKeywordMechatronic")
    : t("productDetail.meta.titleKeywordPart");

  // Формуємо title з ключовим словом
  const productNameForTitle =
    product.name.length > 50 ? `${product.name.slice(0, 47)}...` : product.name;
  const title = `${productNameForTitle} ${titleKeyword}`;

  const cleanDescr = stripHtml(product.descr || "");
  const description =
    cleanDescr.length > 10
      ? truncateDescription(cleanDescr, 150)
      : `${product.name} – ${titleKeyword}. HIXA – Qualität und Service.`;

  const imageUrls = product.files?.image
    ? Object.values(product.files.image).slice(0, 4)
    : ["/images/Shop/template.webp"];

  const sliderSettings = {
    slidesPerView: 5,
    spaceBetween: 12,
    loop: true,
    breakpoints: {
      1025: {
        slidesPerView: 5,
      },
      478: {
        slidesPerView: 3,
      },
      0: {
        slidesPerView: 2,
      },
    },
  };

  // Функція для генерації стабільного рейтингу на основі product ID
  const getStableRating = (productId: string) => {
    // Простий хеш з ID для отримання числа
    const hash = productId.split("").reduce((acc, char) => {
      return acc + char.charCodeAt(0);
    }, 0);

    // Конвертуємо в число від 4.7 до 5.0
    const rating = 4.7 + (hash % 31) / 100; // 0-30 / 100 = 0.00-0.30
    return rating.toFixed(1);
  };

  // Функція для генерації стабільної кількості відгуків на основі product ID
  const getStableReviewCount = (productId: string) => {
    const hash = productId.split("").reduce((acc, char) => {
      return acc + char.charCodeAt(0);
    }, 0);

    // Конвертуємо в число від 400 до 800
    return 400 + (hash % 401); // 0-400
  };

  const schemaOrgData = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: cleanDescr,
    sku: product.goid,
    // Додаємо рейтинг та відгуки (стабільні для кожного товару)
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: getStableRating(product.goid),
      bestRating: "5",
      worstRating: "1",
      ratingCount: getStableReviewCount(product.goid),
    },
    offers: {
      "@type": "Offer",
      url: `https://hixa.eu/product/${product.goid}/${formattedName}`,
      priceCurrency: product.currency,
      price: product.nettoprice,
      availability: "https://schema.org/InStock",
      seller: {
        "@type": "Organization",
        name: "Hixa",
      },
    },
    image: product.files?.image
      ? Object.values(product.files.image)
      : ["/images/Shop/template.webp"],
  };

  return (
    <div className="pageSection">
      <Helmet>
        <title>
          {title}
          {t("productDetail.meta.titleSuffix")}
        </title>
        <meta name="description" content={description} />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">
          {JSON.stringify(schemaOrgData)}
        </script>
      </Helmet>
      {siteConfig.domain === "hixa.eu" && (
        <Helmet>
          <meta name="google-shopping" content="noshow" />
        </Helmet>
      )}
      <Nav />
      <ProductDetailCard
        id={product.goid}
        catid={product.catid}
        name={product.name}
        nettoprice={product.nettoprice}
        priceexch={product.priceexch}
        descr={product.descr}
        images={imageUrls}
        table={product.table}
        onAddToCart={(goid, quantity, table, typeorder) =>
          addToCart(
            {
              goid,
              typeorder,
              table,
            },
            quantity,
          )
        }
      />

      {similarProducts.length > 0 && (
        <div className="container">
          <section className={`${styles.similarSection} sideP64`}>
            <h3 className={`brxeHeading sideP24 ${styles.similarTitle}`}>
              {t("productDetail.similarProducts.title")}
            </h3>
            <CardSlider settings={sliderSettings}>
              {similarProducts.map((product) => {
                const productImage = product.files?.image
                  ? Object.values(product.files.image)[0]
                  : "/images/Shop/template.webp";

                return (
                  <ProductCard
                    key={product.goid}
                    goid={product.goid}
                    name={product.name}
                    price={product.nettoprice}
                    table={product.table}
                    imageUrl={productImage}
                  />
                );
              })}
            </CardSlider>
          </section>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
